import { padStart } from "lodash";

export type VttDialog = {
  index?: string;
  time: string;
  startTimeInSeconds: number;
  endTimeInSeconds: number;
  text: string;
};

/**
 * Converts VTT timestamp to seconds
 * @param timestamp - Format: "HH:MM:SS.mmm"
 * @returns Time in seconds
 */
function timestampToSeconds(timestamp: string): number {
  const [time, milliseconds = "0"] = timestamp.split(".");
  const [hours, minutes, seconds] = time.split(":").map(Number);
  return hours * 3600 + minutes * 60 + seconds + Number(milliseconds) / 1000;
}

/**
 * Cleans VTT text by removing voice tags and decoding HTML entities
 * @param text - Raw VTT text with possible voice tags and HTML entities
 * @returns Cleaned text
 */
function cleanVttText(text: string): string {
  return (
    text
      // Remove voice tags like <v Speaker 1> and </v>
      .replace(/<v[^>]*>/g, "")
      .replace(/<\/v>/g, "")
      // Decode common HTML entities
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&amp;/g, "&")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      // Remove any remaining HTML tags
      .replace(/<[^>]*>/g, "")
      .trim()
  );
}

export function parseVtt(vtt: string): VttDialog[] {
  const lines = vtt.split("\n");
  const result: VttDialog[] = [];

  let i = 0;

  // Skip WEBVTT header and any metadata
  while (i < lines.length) {
    const line = lines[i].trim();
    if (line === "WEBVTT" || line === "" || line.startsWith("NOTE")) {
      i++;
      continue;
    }
    // If we find content (either an index number or timestamp), break
    if (line && (line.includes(" --> ") || /^\d+$/.test(line))) {
      break;
    }
    i++;
  }

  // Process VTT cues
  while (i < lines.length) {
    // Skip empty lines
    if (!lines[i]?.trim()) {
      i++;
      continue;
    }

    let index: string | undefined;
    let time: string;
    const textLines: string[] = [];

    // Check if current line is an index (just a number)
    if (/^\d+$/.test(lines[i].trim())) {
      index = lines[i].trim();
      i++;
    }

    // Next line should be timestamp
    if (i < lines.length && lines[i].includes(" --> ")) {
      time = lines[i].trim();
      i++;

      // Collect text lines until we hit an empty line or end of file
      while (i < lines.length && lines[i].trim() !== "") {
        textLines.push(lines[i]);
        i++;
      }

      if (textLines.length > 0) {
        const text = textLines.join("\n");

        // Parse start and end times
        const [startTime, endTime] = time.split(" --> ");
        const startTimeInSeconds = timestampToSeconds(startTime.trim());
        const endTimeInSeconds = timestampToSeconds(endTime.trim());

        // Clean the text
        const cleanedText = cleanVttText(text);

        result.push({
          index,
          time,
          startTimeInSeconds,
          endTimeInSeconds,
          text: cleanedText,
        });
      }
    } else {
      // Invalid format, skip this line
      i++;
    }
  }

  return result;
}

export function parseVttTime(time: string) {
  const [start] = time.split(" --> ");
  const [startWithoutMs] = start.split(".");
  const [hours, minutes, seconds] = startWithoutMs.split(":");
  const minutesWithHours = padStart(
    String(parseInt(hours) * 60 + parseInt(minutes)),
    2,
    "0",
  );
  const secondsPadded = padStart(seconds, 2, "0");
  return `${hours}:${minutesWithHours}:${secondsPadded}`;
}
